import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import * as XLSX from "xlsx";

import {
  EntityRelationsApi,
  Log,
  PLURAL_KEY,
  RydeUiUnit,
  TEntityRelationsMap,
  useHasPermissionsCheck,
} from "@ryde/shared";

import {
  getRelatedEntitiesExcelFilename,
  mapRelationsMapToRows,
  RELATED_ENTITIES_DRAWER_OPEN_MS,
  RELATED_ENTITIES_EXCEL_SHEET_NAME,
  RELATED_ENTITIES_IDS_JOIN_DELIMITER,
} from "./parts";

export const useRelatedEntitiesDrawer = ({
  entity,
  entityId,
}: {
  entity: RydeUiUnit;
  entityId: number;
}) => {
  const { t } = useTranslation();
  const { hasPermissionsCheck } = useHasPermissionsCheck();
  const [isLoading, setIsLoading] = useState(true);
  const [relationsMap, setRelationsMap] = useState<TEntityRelationsMap>({});

  const fetchRelations = useEvent(async () => {
    setIsLoading(true);
    try {
      const data = await EntityRelationsApi.getRelations(entity, entityId);
      setRelationsMap(data ?? {});
    } catch (err) {
      Log.error("Failed to fetch entity relations", err);
      setRelationsMap({});
    } finally {
      setIsLoading(false);
    }
  });

  useEffect(() => {
    const timerId = window.setTimeout(() => {
      void fetchRelations();
    }, RELATED_ENTITIES_DRAWER_OPEN_MS);

    return () => window.clearTimeout(timerId);
  }, [entity, entityId, fetchRelations]);

  const rows = useMemo(
    () =>
      mapRelationsMapToRows(relationsMap, relationEntity =>
        hasPermissionsCheck({
          entity: relationEntity,
          action: "read",
        }),
      ),
    [hasPermissionsCheck, relationsMap],
  );

  const downloadExcel = useCallback(() => {
    const sheetData = rows.map(row => ({
      [t("general.RELATION_ENTITY")]: t(`${row.relation}.${PLURAL_KEY}`),
      [t("general.RELATION_IDS")]: row.ids.join(
        RELATED_ENTITIES_IDS_JOIN_DELIMITER,
      ),
    }));

    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      RELATED_ENTITIES_EXCEL_SHEET_NAME,
    );
    XLSX.writeFile(workbook, getRelatedEntitiesExcelFilename(entity, entityId));
  }, [entity, entityId, rows, t]);

  return {
    rows,
    isLoading,
    refetch: fetchRelations,
    downloadExcel,
  };
};
